/**
 * Admin page for managing embeddable chat widgets.
 * Lists all widgets, allows creation, editing, and viewing embed code.
 */

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useState, useCallback } from 'react'
import {
  Plus, Copy, Check, Trash2, RotateCw,
  Globe, Palette, MessageSquare, Code, BarChart3, Settings2,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { NativeSelect } from '@/components/ui/NativeSelect'
import {
  Modal, ModalContent, ModalHeader, ModalTitle,
  ModalBody, ModalFooter,
} from '@/components/ui/Modal'
import * as Tabs from '@radix-ui/react-tabs'
import { useWidgets, type WidgetData, type WidgetAnalyticsData } from '@/hooks/useWidgets'
import { WidgetDestinationsEditor } from './WidgetDestinationsEditor'
import { usePersonas } from '@/hooks/usePersonas'
import { usePermission } from '@/hooks/usePermission'
import { useAuthStore } from '@/stores/authStore'
import { parseAllowedDomains, normalizeAllowedDomains } from '@/utils/widgetDomains'
import { BrandingTab } from './widget/BrandingTab'
export { LAUNCHER_HINT, LAUNCHER_HIDDEN_HINT } from './widget/BrandingTab'

/** SA-353: one source for both Allowed Domains textareas so the copy cannot drift. */
export const ALLOWED_DOMAINS_HINT =
  'One host per line, as host or *.host. No scheme or path. A bare apex (example.com) does not cover its subdomains — use *.example.com for that.'

/** SA-353: pages opened from file:// send Origin: null, which no allowlist entry can match. */
export const FILE_ORIGIN_HINT =
  'Test pages must be served over http(s). A page opened from file:// sends Origin: null, which no allowed domain can match.'

/** SA-361: one source for the owner copy so the tests and the two render sites cannot drift. */
export const OWNER_LABEL = (name: string) => `Owner: ${name}`
export const OWNED_BY_LABEL = (name: string) => `Owned by ${name}`

const TEAM_HANDLING_HINT =
  "Sent once, on the visitor's first message after a team member takes over. Leave empty for the default."

const ESCALATION_MESSAGE_HINT =
  'Sent when an escalation keyword hands the chat to the team. Leave blank to use the default.'

/** SA-361: shown in place of Save when the viewer may see but not change the widget. */
export const READ_ONLY_HINT = 'Read-only: changing this widget requires Edit All Widgets.'

/** SA-436: the site rule (spec D7) — one source for the copy so the test cannot drift. */
export const SITE_ORIGINS_HINT =
  'One full origin per line, e.g. https://www.example.com. These count as "this site" when the widget tells your pages from external ones. An international domain must be entered in its ASCII (punycode) form — saving will tell you which.'
export const SITE_REWRITE_HINT =
  'Opens source and answer links on those origins on the page the widget is embedded on — for mirrors and demo copies. Off = links open where they point.'
/** SA-438 (spec D1, D10): the "Take me there" opt-in — one source for the copy so the test cannot drift. */
export const SITE_NAVIGATION_HINT =
  'Adds a "Take me there" button on sources from this site that takes the visitor to the page in the same tab, scrolled to the passage the answer came from. Off by default.'
/** SA-436 (spec D1): how [Source N] markers in replies render in the widget. */
export const SOURCE_MARKERS_HINT =
  'Replies cite passages as [Source N]. Link them to the grouped source under the answer, or remove them.'

/** SA-440: the public window.nrWidget API, shown beside the embed snippet. */
export const PUBLIC_API_SNIPPET = `nrWidget.open()      // show the chat
nrWidget.close()     // hide it
nrWidget.toggle()
nrWidget.isOpen()    // true while the panel is on screen

const off = nrWidget.on('ready', () => { /* the widget is live */ })
nrWidget.on('open',  () => {})
nrWidget.on('close', () => {})
nrWidget.on('message', e => {})           // { role, id } — never the text
nrWidget.on('navigate', e => {            // take navigation over
  e.preventDefault()
  myRouter.push(e.url)
})
off()                                      // stop listening`

export function WidgetManagementPage() {
  const {
    widgets, isLoading, error,
    createWidget, updateWidget, deleteWidget, rotateSecret, fetchAnalytics,
  } = useWidgets()
  const { personas } = usePersonas()

  // SA-361 oversight scopes. Write implies read for display purposes.
  const canReadAll = usePermission('widgets:read:all')
  const canWriteAll = usePermission('widgets:write:all')
  const currentUserId = useAuthStore(s => s.user?.id)
  const showOwner = canReadAll || canWriteAll

  // The owner may always change their own widget; anyone else needs widgets:write:all.
  const canMutate = useCallback(
    (w: WidgetData) => w.userId === currentUserId || canWriteAll,
    [currentUserId, canWriteAll],
  )

  // Create modal
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', personaId: '', domains: '', locale: 'en' })
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Edit modal
  const [editWidget, setEditWidget] = useState<WidgetData | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Analytics
  const [analytics, setAnalytics] = useState<WidgetAnalyticsData | null>(null)

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<WidgetData | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Embed code copy state
  const [copied, setCopied] = useState(false)

  const handleCreate = useCallback(async () => {
    if (!createForm.name.trim() || !createForm.personaId) return
    setIsCreating(true)
    setCreateError(null)
    try {
      await createWidget({
        name: createForm.name.trim(),
        defaultPersonaId: createForm.personaId,
        allowedDomains: parseAllowedDomains(createForm.domains),
        defaultLocale: createForm.locale,
      })
      setShowCreate(false)
      setCreateForm({ name: '', personaId: '', domains: '', locale: 'en' })
    } catch (err: any) {
      setCreateError(err.message)
    } finally {
      setIsCreating(false)
    }
  }, [createForm, createWidget])

  const handleSave = useCallback(async () => {
    if (!editWidget) return
    setIsSaving(true)
    setEditError(null)
    try {
      // `site_config` rides every save, not just an edit to it, so the server
      // re-normalizes the whole document each time: a row stored malformed
      // (or by an older shape) becomes the empty rule on the next unrelated
      // save, rather than lingering until someone opens this section.
      await updateWidget(editWidget.id, {
        ...editWidget,
        allowedDomains: normalizeAllowedDomains(editWidget.allowedDomains),
        siteConfig: {
          ...editWidget.siteConfig,
          origins: editWidget.siteConfig.origins.map((o) => o.trim()).filter(Boolean),
        },
      })
      setEditWidget(null)
    } catch (err: any) {
      setEditError(err.message)
    } finally {
      setIsSaving(false)
    }
  }, [editWidget, updateWidget])

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteWidget(deleteTarget.id)
      setDeleteTarget(null)
    } catch {
      // Error handled by hook
    } finally {
      setIsDeleting(false)
    }
  }, [deleteTarget, deleteWidget])

  const handleOpenEdit = useCallback(async (widget: WidgetData) => {
    setEditWidget({ ...widget })
    setEditError(null)
    // Load analytics
    try {
      const a = await fetchAnalytics(widget.id, '7d')
      setAnalytics(a)
    } catch {
      setAnalytics(null)
    }
  }, [fetchAnalytics])

  const handleCopyEmbed = useCallback((widgetId: string) => {
    const baseUrl = window.location.origin
    const snippet = `<script\n  src="${baseUrl}/widget/embed.js"\n  data-widget-id="${widgetId}"\n  async\n></script>`
    navigator.clipboard.writeText(snippet)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [])

  const updateEditField = useCallback((field: string, value: any) => {
    setEditWidget(prev => prev ? { ...prev, [field]: value } : null)
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading widgets...</div>
      </div>
    )
  }

  return (
    <PageFrame width="list" className="space-y-6">
      <PageHeader
        title="Embeddable Widgets"
        subtitle="Add AI chat to any website with a single script tag"
        actions={
          <Button onClick={() => setShowCreate(true)} leftIcon={<Plus className="w-4 h-4" />}>
            Create Widget
          </Button>
        }
      />

      {error && (
        <div className="bg-destructive/10 text-destructive rounded-lg p-4 mb-4">{error}</div>
      )}

      {/* Widget List */}
      {widgets.length === 0 ? (
        <div className="text-center py-12 bg-muted rounded-lg">
          <Globe className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <h3 className="text-lg font-medium text-foreground mb-1">No widgets yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Create your first widget to embed AI chat on your website</p>
          <Button onClick={() => setShowCreate(true)} leftIcon={<Plus className="w-4 h-4" />}>
            Create Widget
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {widgets.map(widget => (
            <div
              key={widget.id}
              className="bg-card border border-border rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${widget.isActive ? 'bg-success' : 'bg-warning'}`} />
                  <div className="min-w-0">
                    <h3 className="font-medium text-foreground truncate">{widget.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {widget.allowedDomains.length > 0
                        ? widget.allowedDomains.join(', ')
                        : 'No domains configured'}
                    </p>
                    {showOwner && widget.ownerName && (
                      <p className="text-xs text-muted-foreground mt-0.5">{OWNER_LABEL(widget.ownerName)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyEmbed(widget.id)}
                    leftIcon={copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  >
                    {copied ? 'Copied' : 'Embed'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(widget)}>
                    {canMutate(widget) ? 'Edit' : 'View'}
                  </Button>
                  {canMutate(widget) && (
                    <Button variant="ghost" size="sm" aria-label="Delete widget" onClick={() => setDeleteTarget(widget)}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create Modal ──────────────────────────────────────────── */}
      <Modal open={showCreate} onOpenChange={setShowCreate}>
        <ModalContent size="md">
          <ModalHeader>
            <ModalTitle>Create Widget</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {createError && (
              <div className="bg-destructive/10 text-destructive rounded-sm p-3 mb-4 text-sm">{createError}</div>
            )}
            <div className="space-y-4">
              <Input
                label="Widget Name"
                value={createForm.name}
                onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Main Website Chat"
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Persona
                </label>
                <select
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-card"
                  value={createForm.personaId}
                  onChange={(e) => setCreateForm(f => ({ ...f, personaId: e.target.value }))}
                >
                  <option value="">Select a persona...</option>
                  {Object.values(personas).map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Allowed Domains (one per line)
                </label>
                <textarea
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-card"
                  rows={3}
                  value={createForm.domains}
                  onChange={(e) => setCreateForm(f => ({ ...f, domains: e.target.value }))}
                  placeholder={"example.com\n*.example.com"}
                />
                <p className="text-xs text-muted-foreground mt-1">{ALLOWED_DOMAINS_HINT}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Default Language
                </label>
                <select
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-card"
                  value={createForm.locale}
                  onChange={(e) => setCreateForm(f => ({ ...f, locale: e.target.value }))}
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                </select>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              isLoading={isCreating}
              disabled={!createForm.name.trim() || !createForm.personaId}
            >
              Create Widget
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Edit Modal (Tabbed) ───────────────────────────────────── */}
      <Modal open={!!editWidget} onOpenChange={(open) => { if (!open) setEditWidget(null) }}>
        <ModalContent size="lg">
          <ModalHeader>
            <ModalTitle>Edit Widget: {editWidget?.name}</ModalTitle>
            {showOwner && editWidget?.ownerName && (
              <p className="text-sm text-muted-foreground">{OWNED_BY_LABEL(editWidget.ownerName)}</p>
            )}
          </ModalHeader>
          <ModalBody>
            {editError && (
              <div className="bg-destructive/10 text-destructive rounded-sm p-3 mb-4 text-sm">{editError}</div>
            )}
            {editWidget && (
              <Tabs.Root defaultValue="general">
                <Tabs.List className="flex border-b border-border mb-4 gap-1 overflow-x-auto">
                  {[
                    { value: 'general', icon: Settings2, label: 'General' },
                    { value: 'appearance', icon: Palette, label: 'Branding' },
                    { value: 'behavior', icon: MessageSquare, label: 'Behavior' },
                    { value: 'embed', icon: Code, label: 'Embed Code' },
                    { value: 'analytics', icon: BarChart3, label: 'Analytics' },
                  ].map(tab => (
                    <Tabs.Trigger
                      key={tab.value}
                      value={tab.value}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary whitespace-nowrap"
                    >
                      <tab.icon className="w-3.5 h-3.5" />
                      {tab.label}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>

                {/* General Tab */}
                <Tabs.Content value="general" className="space-y-4">
                  <Input
                    label="Widget Name"
                    value={editWidget.name}
                    onChange={(e) => updateEditField('name', e.target.value)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Persona</label>
                    <select
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-card"
                      value={editWidget.defaultPersonaId || ''}
                      onChange={(e) => updateEditField('defaultPersonaId', e.target.value)}
                    >
                      <option value="">Select persona...</option>
                      {Object.values(personas).map((p: any) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Allowed Domains</label>
                    <textarea
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-card"
                      rows={3}
                      value={editWidget.allowedDomains.join('\n')}
                      onChange={(e) => updateEditField('allowedDomains', e.target.value.split('\n').map(d => d.trim()).filter(Boolean))}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{ALLOWED_DOMAINS_HINT}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Site origins</label>
                    <textarea
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-card"
                      rows={3}
                      placeholder="https://www.example.com"
                      value={editWidget.siteConfig.origins.join('\n')}
                      onChange={(e) => updateEditField('siteConfig', { ...editWidget.siteConfig, origins: e.target.value.split('\n') })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{SITE_ORIGINS_HINT}</p>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={editWidget.siteConfig.rewriteToEmbeddingOrigin}
                        onChange={(e) => updateEditField('siteConfig', { ...editWidget.siteConfig, rewriteToEmbeddingOrigin: e.target.checked })}
                      />
                      Open same-site pages on this site
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">{SITE_REWRITE_HINT}</p>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={editWidget.siteConfig.navigationEnabled}
                        onChange={(e) => updateEditField('siteConfig', { ...editWidget.siteConfig, navigationEnabled: e.target.checked })}
                      />
                      Offer “Take me there” on same-site sources
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">{SITE_NAVIGATION_HINT}</p>
                  </div>
                  <WidgetDestinationsEditor
                    value={editWidget.siteConfig.destinations}
                    onChange={(destinations) => updateEditField('siteConfig', { ...editWidget.siteConfig, destinations })}
                  />
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-foreground">Active</div>
                      <div className="text-xs text-muted-foreground">Widget will respond to requests when active</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={editWidget.isActive}
                      onChange={(e) => updateEditField('isActive', e.target.checked)}
                      className="h-4 w-4"
                    />
                  </div>
                </Tabs.Content>

                {/* Branding Tab */}
                <Tabs.Content value="appearance" className="space-y-4">
                  <BrandingTab
                    value={editWidget.themeConfig}
                    onChange={(next) => updateEditField('themeConfig', next)}
                    launcher={editWidget.launcherConfig}
                    onLauncherChange={(next) => updateEditField('launcherConfig', next)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Position</label>
                    <select
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-card"
                      value={editWidget.position}
                      onChange={(e) => updateEditField('position', e.target.value)}
                    >
                      <option value="bottom-right">Bottom Right</option>
                      <option value="bottom-left">Bottom Left</option>
                    </select>
                  </div>
                  <Input
                    label="Welcome Message (English)"
                    value={editWidget.welcomeMessage?.en || ''}
                    onChange={(e) => updateEditField('welcomeMessage', { ...editWidget.welcomeMessage, en: e.target.value })}
                  />
                  <Input
                    label="Welcome Message (Spanish)"
                    value={editWidget.welcomeMessage?.es || ''}
                    onChange={(e) => updateEditField('welcomeMessage', { ...editWidget.welcomeMessage, es: e.target.value })}
                  />
                </Tabs.Content>

                {/* Behavior Tab */}
                <Tabs.Content value="behavior" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { field: 'showSources', label: 'Show Sources' },
                      { field: 'showPoweredBy', label: 'Show Powered By' },
                      { field: 'enableFileUpload', label: 'File Uploads' },
                      { field: 'enableNotificationSound', label: 'Notification Sound' },
                      { field: 'enableVoice', label: 'Voice (PTT)' },
                      { field: 'escalationEnabled', label: 'Escalation' },
                    ].map(({ field, label }) => (
                      <label key={field} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={(editWidget as any)[field]}
                          onChange={(e) => updateEditField(field, e.target.checked)}
                          className="h-4 w-4"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <NativeSelect
                    label="Source markers in replies"
                    hint={SOURCE_MARKERS_HINT}
                    value={editWidget.sourceMarkers}
                    onChange={(e) => updateEditField('sourceMarkers', e.target.value === 'strip' ? 'strip' : 'link')}
                  >
                    <option value="link">Link to the source</option>
                    <option value="strip">Remove</option>
                  </NativeSelect>
                  <Input
                    label="Auto-Open Delay (seconds, 0 = never)"
                    type="number"
                    value={String(editWidget.autoOpenDelay)}
                    onChange={(e) => updateEditField('autoOpenDelay', parseInt(e.target.value) || 0)}
                  />
                  <Input
                    label="Session Timeout (hours)"
                    type="number"
                    value={String(editWidget.sessionTimeoutHours)}
                    onChange={(e) => updateEditField('sessionTimeoutHours', parseFloat(e.target.value) || 24)}
                  />
                  <Input
                    label="Message when a team member takes over (English)"
                    hint={TEAM_HANDLING_HINT}
                    placeholder="Thanks, your message has reached the team. Someone will reply here shortly."
                    value={editWidget.teamHandlingMessage?.en || ''}
                    onChange={(e) => updateEditField('teamHandlingMessage', { ...editWidget.teamHandlingMessage, en: e.target.value })}
                  />
                  <Input
                    label="Message when a team member takes over (Spanish)"
                    hint={TEAM_HANDLING_HINT}
                    placeholder="Gracias, tu mensaje ha llegado al equipo. Alguien te responderá aquí en breve."
                    value={editWidget.teamHandlingMessage?.es || ''}
                    onChange={(e) => updateEditField('teamHandlingMessage', { ...editWidget.teamHandlingMessage, es: e.target.value })}
                  />
                  {editWidget.escalationEnabled && (
                    <>
                    <Input
                      label="Escalation message (English)"
                      hint={ESCALATION_MESSAGE_HINT}
                      placeholder="I'm connecting you with a team member who can help."
                      value={editWidget.escalationMessage?.en || ''}
                      onChange={(e) => updateEditField('escalationMessage', { ...editWidget.escalationMessage, en: e.target.value })}
                    />
                    <Input
                      label="Escalation message (Spanish)"
                      hint={ESCALATION_MESSAGE_HINT}
                      placeholder="Te estoy conectando con un miembro del equipo que puede ayudarte."
                      value={editWidget.escalationMessage?.es || ''}
                      onChange={(e) => updateEditField('escalationMessage', { ...editWidget.escalationMessage, es: e.target.value })}
                    />
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Escalation Keywords (one per line)
                      </label>
                      <textarea
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-card"
                        rows={3}
                        value={editWidget.escalationKeywords.join('\n')}
                        onChange={(e) => updateEditField('escalationKeywords', e.target.value.split('\n').filter(Boolean))}
                        placeholder={"human\nagent\nhelp"}
                      />
                    </div>
                    </>
                  )}
                </Tabs.Content>

                {/* Embed Code Tab */}
                <Tabs.Content value="embed" className="space-y-4">
                  <div>
                    <p className="text-sm text-foreground mb-3">
                      Copy this code and paste it before the <code className="bg-muted px-1 rounded-sm">&lt;/body&gt;</code> tag on every page where you want the chat widget to appear.
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">{FILE_ORIGIN_HINT}</p>
                    {/* basis: hardcoded-surface guard — this <pre> renders an embed
                        snippet as a terminal-style block. bg-gray-900/text-green-400 is an
                        intentional theme-INDEPENDENT surface — keep fixed colors in both
                        modes; do NOT tokenize (M1 invisibility regression). */}
                    <pre className="bg-gray-900 text-green-400 rounded-lg p-4 text-sm overflow-x-auto">
{`<script
  src="${window.location.origin}/widget/embed.js"
  data-widget-id="${editWidget.id}"
  async
></script>`}
                    </pre>
                    <Button
                      className="mt-3"
                      variant="secondary"
                      onClick={() => handleCopyEmbed(editWidget.id)}
                      leftIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    >
                      {copied ? 'Copied!' : 'Copy to Clipboard'}
                    </Button>
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-medium text-foreground mb-2">Widget ID</h4>
                    <code className="text-xs bg-muted px-2 py-1 rounded-sm">{editWidget.id}</code>
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-medium text-foreground mb-2">Control it from your page</h4>
                    <p className="text-xs text-muted-foreground mb-2">
                      The embed publishes <code className="bg-muted px-1 rounded-sm">window.nrWidget</code>.
                      It never exposes conversation text, the visitor id or the session token.
                    </p>
                    <pre
                      data-testid="nr-widget-api-snippet"
                      className="bg-muted rounded-sm p-3 text-xs overflow-x-auto whitespace-pre-wrap"
                    >{PUBLIC_API_SNIPPET}</pre>
                    <p className="text-xs text-muted-foreground mt-2">
                      To call it before the widget has loaded, see the help page for the one-line stub.
                    </p>
                  </div>
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-medium text-foreground mb-2">Content Security Policy (CSP)</h4>
                    <p className="text-xs text-muted-foreground mb-2">
                      If your website uses a strict CSP, add these directives:
                    </p>
                    <pre className="bg-muted rounded-sm p-3 text-xs overflow-x-auto whitespace-pre-wrap">
{`script-src 'self' ${window.location.origin};
connect-src 'self' ${window.location.origin}${editWidget.enableVoice ? ' https://api.elevenlabs.io wss://api.deepgram.com' : ''};
style-src 'self' 'unsafe-inline';`}
                    </pre>
                  </div>
                  {canMutate(editWidget) && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-foreground mb-2">Security</h4>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={async () => {
                          if (confirm('This will invalidate all active visitor sessions. Continue?')) {
                            await rotateSecret(editWidget.id)
                          }
                        }}
                        leftIcon={<RotateCw className="w-3.5 h-3.5" />}
                      >
                        Rotate Secret Key
                      </Button>
                    </div>
                  )}
                </Tabs.Content>

                {/* Analytics Tab */}
                <Tabs.Content value="analytics" className="space-y-4">
                  {analytics ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {[
                        { label: 'Conversations', value: analytics.conversationCount },
                        { label: 'User Messages', value: analytics.messageCountUser },
                        { label: 'AI Messages', value: analytics.messageCountAssistant },
                        { label: 'Unique Visitors', value: analytics.uniqueVisitors },
                        { label: 'Avg Msgs/Conv', value: analytics.avgMessagesPerConversation },
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-muted rounded-lg p-3">
                          <div className="text-xs text-muted-foreground uppercase">{label}</div>
                          <div className="text-xl font-semibold text-foreground mt-1">{value}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8">No analytics data yet</div>
                  )}
                  <p className="text-xs text-muted-foreground">Last 7 days</p>
                </Tabs.Content>
              </Tabs.Root>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setEditWidget(null)}>Cancel</Button>
            {editWidget && canMutate(editWidget) ? (
              <Button onClick={handleSave} isLoading={isSaving}>Save Changes</Button>
            ) : (
              <span className="text-xs text-muted-foreground">{READ_ONLY_HINT}</span>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Delete Confirmation ────────────────────────────────────── */}
      <Modal open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Delete Widget</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-foreground">
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This will disable the widget on all embedded sites.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} isLoading={isDeleting}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageFrame>
  )
}
